import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

interface Partner {
  id: string;
  name: string;
  promoCodes: string[];
  category: 'startup' | 'investor' | 'both';
  users: number;
  isActive: boolean;
  createdAt: Date | null;
  lastUsed: Date | null;
}

const AdminPartnersPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingPartnerId, setEditingPartnerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [promoCodes, setPromoCodes] = useState<string[]>(['']);
  const [category, setCategory] = useState<'startup' | 'investor' | 'both'>('startup');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const listPartnersFunction = httpsCallable(functions, 'listPartners');
      const result = await listPartnersFunction({});
      const data = result.data as any;

      if (data?.success && data.partners) {
        const parseDate = (d: any) => (d ? new Date(d) : null);
        const partnersWithDates = data.partners.map((p: any) => ({
          ...p,
          createdAt: parseDate(p.createdAt),
          lastUsed: parseDate(p.lastUsed),
          isActive: p.isActive !== false,
          category: p.category || 'startup',
        }));
        setPartners(partnersWithDates);
      }
    } catch (err: any) {
      console.error('Error loading partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreating(true);
      setError(null);

      const codes = promoCodes.map(c => c.trim()).filter(c => c.length > 0);
      if (codes.length === 0) throw new Error('At least one promo code is required');

      if (editingPartnerId) {
        const updatePartnerFunction = httpsCallable(functions, 'updatePartner');
        const result = await updatePartnerFunction({
          partnerId: editingPartnerId,
          name,
          promoCodes: codes,
          category,
          isActive,
        });
        const data = result.data as any;
        if (!data?.success) throw new Error(data?.error || 'Failed to update partner');
      } else {
        const createPartnerFunction = httpsCallable(functions, 'createPartner');
        const result = await createPartnerFunction({
          name,
          promoCodes: codes,
          category,
          isActive,
        });
        const data = result.data as any;
        if (!data?.success) throw new Error(data?.error || 'Failed to create partner');
      }

      await loadPartners();
      resetForm();
      setShowAddModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save partner');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setName('');
    setPromoCodes(['']);
    setCategory('startup');
    setIsActive(true);
    setEditingPartnerId(null);
  };

  const handleEditClick = (partner: Partner) => {
    setName(partner.name);
    setPromoCodes(partner.promoCodes);
    setCategory(partner.category);
    setIsActive(partner.isActive);
    setEditingPartnerId(partner.id);
    setShowAddModal(true);
  };

  const handleToggleStatus = async (partnerId: string, currentStatus: boolean) => {
    try {
      const toggleFunction = httpsCallable(functions, 'togglePartnerStatus');
      await toggleFunction({ partnerId, isActive: !currentStatus });
      await loadPartners();
    } catch (err: any) {
      alert('Error toggling status: ' + err.message);
    }
  };

  const handleDeletePartner = async (partnerId: string) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      const deleteFunction = httpsCallable(functions, 'deletePartner');
      await deleteFunction({ partnerId });
      await loadPartners();
    } catch (err: any) {
      alert('Error deleting partner: ' + err.message);
    }
  };

  const copyUTMLink = (partnerName: string, promoCode: string, cat: string) => {
    const signupType = cat === 'investor' ? 'investor' : 'startup';
    const link = `https://prairi.com/signup/${signupType}?utm_source=${encodeURIComponent(partnerName)}&utm_medium=partner&utm_campaign=${encodeURIComponent(promoCode)}`;
    navigator.clipboard.writeText(link).then(() => alert('UTM Link copied!'));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d0d0d]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-[#0d0d0d] flex flex-col items-center pt-8 ${showAddModal ? 'overflow-hidden' : ''}`}
    >
      <div
        className={`w-full max-w-7xl px-6 py-10 transition-all ${showAddModal ? 'blur-sm grayscale-[0.5]' : ''}`}
      >
        <h1 className="text-center text-4xl font-bold text-gray-800 mb-12">Partner Management</h1>

        <div className="flex justify-center mb-12">
          <button
            onClick={() => setShowAddModal(true)}
            className="px-8 py-3 bg-[#0d0d0d] text-white font-bold rounded-lg border border-emerald-500/50 hover:border-emerald-500 transition-all shadow-[0_0_15px_-3px_rgba(146,208,80,0.3)] hover:shadow-[0_0_20px_-3px_rgba(146,208,80,0.5)]"
          >
            Add New Partner
          </button>
        </div>

        {/* Existing Partner Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {partners.map(partner => (
            <div
              key={partner.id}
              className="bg-[#f8fff9] rounded-3xl p-8 shadow-lg border border-emerald-50/50 flex flex-col relative"
            >
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-gray-800">{partner.name}</h3>
                <span
                  className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${partner.isActive ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}
                >
                  {partner.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="space-y-4 mb-8 flex-grow">
                <p className="text-gray-500 font-medium">
                  Category: <span className="text-gray-800 capitalize">{partner.category}</span>
                </p>
                <p className="text-gray-500 font-medium">
                  Users: <span className="text-gray-800">{partner.users}</span>
                </p>
                <div>
                  <p className="text-gray-500 font-medium mb-3">Promo Codes:</p>
                  <div className="flex flex-col space-y-3">
                    {partner.promoCodes.map(code => (
                      <div key={code} className="flex items-center justify-between">
                        <span className="bg-[#eef2ff] text-[#4f46e5] px-4 py-1.5 rounded-lg text-sm font-bold border border-[#e0e7ff]">
                          {code}
                        </span>
                        <button
                          onClick={() => copyUTMLink(partner.name, code, partner.category)}
                          className="text-xs font-bold text-gray-500 hover:text-emerald-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-emerald-50 transition-all"
                        >
                          Copy UTM link
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex space-x-2 pt-6 border-t border-emerald-100/50">
                <button
                  onClick={() => handleEditClick(partner)}
                  className="flex-1 px-3 py-2 border border-blue-400 text-blue-500 text-xs font-bold rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleToggleStatus(partner.id, partner.isActive)}
                  className={`flex-1 px-3 py-2 border ${partner.isActive ? 'border-amber-400 text-amber-500' : 'border-emerald-400 text-emerald-500'} text-xs font-bold rounded-lg hover:bg-amber-50 transition-colors`}
                >
                  {partner.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDeletePartner(partner.id)}
                  className="flex-1 px-3 py-2 border border-red-400 text-red-500 text-xs font-bold rounded-lg hover:bg-red-50 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL OVERLAY */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-[440px] bg-[#1a1a1a] rounded-[24px] border border-[#a3e635]/40 p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <h2 className="text-white text-2xl font-bold text-center mb-8">
              {editingPartnerId ? 'Edit Partner' : 'Add New Partner'}
            </h2>

            <form onSubmit={handleSavePartner} className="space-y-6">
              {/* Partner Name */}
              <div>
                <label className="text-white font-bold text-sm block mb-2">Partner Name</label>
                <input
                  type="text"
                  className="w-full bg-white rounded-lg p-3 text-black outline-none"
                  placeholder="e.g., New Partner"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-white font-bold text-sm block mb-2">Category</label>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value as any)}
                  className="w-full bg-white rounded-lg p-3 text-black outline-none appearance-none cursor-pointer"
                >
                  <option value="startup">Startup</option>
                  <option value="investor">Investor</option>
                  <option value="both">Both</option>
                </select>
              </div>

              {/* Promo Codes */}
              <div>
                <label className="text-white font-bold text-sm block mb-2">Promo Codes</label>
                {promoCodes.map((code, idx) => (
                  <input
                    key={idx}
                    type="text"
                    className="w-full bg-white rounded-lg p-3 text-black outline-none mb-2"
                    placeholder="e.g., CODE25"
                    value={code}
                    onChange={e => {
                      const newCodes = [...promoCodes];
                      newCodes[idx] = e.target.value;
                      setPromoCodes(newCodes);
                    }}
                    required={idx === 0}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setPromoCodes([...promoCodes, ''])}
                  className="text-[#a3e635] text-xs font-bold hover:underline"
                >
                  + Add Promo Code
                </button>
              </div>

              {/* Active Partner Checkbox */}
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                  className="w-5 h-5 rounded border-none accent-[#a3e635]"
                />
                <span className="text-white font-bold text-sm">Active Partner</span>
              </label>

              {error && <p className="text-red-500 text-xs text-center">{error}</p>}

              {/* Buttons */}
              <div className="flex items-center space-x-6 pt-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 px-8 rounded-lg transition-colors min-w-[160px]"
                >
                  {creating ? 'Saving...' : editingPartnerId ? 'Update Partner' : 'Create Partner'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-white hover:underline font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPartnersPage;
